/* eslint-disable import/max-dependencies */
import { useQuery } from '@apollo/client'
import { BodyText } from '@paypalcorp/pp-react'
import { Router, navigate } from '@reach/router'
import { isNull, merge } from 'lodash-es'
import PageMessage from 'ppbl/components/PageMessage'
import SpinnerAdapter from 'ppbl/components/SpinnerAdapter'
import FooterSection from 'ppbl/components/common/FooterSection'
import HeaderSection from 'ppbl/components/common/HeaderSection'
import graphqlClient from 'ppbl/graphql/graphqlClient'
import { getApplicationWithOfferByIdQuery } from 'ppbl/graphql/offers/queries'
import {
  getApplicationByIdQuery,
  getCreditApplicationDataQuery,
  renewalApplicationPrefillQuery,
} from 'ppbl/graphql/queries'
import { useApplicationContext } from 'ppbl/hooks'
import clientLogger from 'ppbl/utils/client-logger'
import { applicationStateList } from './constants'
import { CaseTypes } from 'ppbl/utils/constants'
import { getLocalizedText } from 'ppbl/utils/locale-helper'
import serverData from 'ppbl/utils/server-data'
import React, { useEffect } from 'react'
import {
  determineSkipGetApplicationCall,
  getProductIdentifier,
  normalizeCreditApplicationResponse,
  handleGraphqlError,
} from '../__utils__'
import CreditFrozenPage from '../creditFrozen-page'
import ErrorPage from '../error-page'
import IneligibleStatePage from '../ineligibleState-page'
import CountryNotSupportedPage from '../countryNotSupported-page'
import IneligiblePage from '../ineligible-page'
import UnsupportedAccountPage from '../unsupportedAccount-page'
import OfferPage from '../offer-page'
import OpenApplicationPage from '../openApplication-page'
import PendingPage from '../pending-page'
import SubmissionLoading from '../submissionLoading-page'
import ThankYouPage from '../thankYou-page'
import ApplicationPage1 from './applicationPages/applicationPage1'
import ApplicationPage2 from './applicationPages/applicationPage2'
import ApplicationPage3 from './applicationPages/applicationPage3'
import ApplicationPage4 from './applicationPages/applicationPage4'
import ApplicationPage5 from './applicationPages/applicationPage5'
import { applicationCompleteRoutes } from './constants/routes'
import { withProgressMeter, withRouteGuard } from './hocs'
import './styles.css'
import SessionTimeout from '../sessionTimeout-page'

type Props = {
  applicationType: string
}

type LoaderProps = {
  default?: boolean
  path?: string
}

const Application: React.FC<Props> = ({ applicationType }) => {
  const applicationId = sessionStorage.getItem('appId')
  const mostRecentCompletedPage = sessionStorage.getItem(
    'mostRecentCompletedPage',
  )
  const applicationSubmitStatus = sessionStorage.getItem(
    'applicationSubmitStatus',
  )
  const { data, updateApplicationContext } = useApplicationContext()

  const skipApplicationQuery =
    !applicationId ||
    determineSkipGetApplicationCall(
      data,
      mostRecentCompletedPage,
      applicationSubmitStatus,
    )

  const getApplicationQuery =
    applicationCompleteRoutes.includes(mostRecentCompletedPage) &&
    applicationSubmitStatus === CaseTypes.PENDING_STIPULATIONS
      ? getApplicationWithOfferByIdQuery
      : getApplicationByIdQuery

  const {
    data: creditApplicationData,
    loading: creditApplicationLoading,
    error: creditApplicationError,
  } = useQuery(getApplicationQuery, {
    variables: {
      applicationId,
    },
    skip: skipApplicationQuery,
  })

  const renewalToken = serverData.startParams?.renewalToken
  const isPaypalLoggedin = !!serverData?.user
  const renewalTokenBurnt = sessionStorage.getItem('renewalTokenBurnt')
  const isPaypalApplication = applicationType === 'paypal'

  const skipPrefillQuery = !(
    ((applicationType === 'app-renewal' && renewalToken) ||
      (applicationType === 'paypal-renewal' && renewalToken) ||
      (applicationType === 'paypal' && isPaypalLoggedin)) &&
    isNull(renewalTokenBurnt)
  )

  const {
    data: prefillApplicationData,
    loading: prefillApplicationLoading,
    error: prefillApplicationError,
  } = useQuery(renewalApplicationPrefillQuery, {
    variables: {
      productIdentifier: getProductIdentifier(applicationType),
      renewalToken,
    },
    skip: skipPrefillQuery,
  })

  const Loader: React.FC<LoaderProps> = () => {
    return (
      <div className="spinner">
        <SpinnerAdapter />
      </div>
    )
  }

  const OfferLoader: React.FC<LoaderProps> = () => {
    return (
      <PageMessage
        type="loading"
        localization={{
          title: getLocalizedText('application.index.loadingTitle'),
        }}
      >
        <BodyText className="body__text">
          {getLocalizedText('application.index.loadingBodyText')}
        </BodyText>
      </PageMessage>
    )
  }

  const AppPage1 = withRouteGuard(
    withProgressMeter(
      {
        max: '5',
        value: '1',
        steps: getLocalizedText('application.index.step1Text'),
        title: getLocalizedText('application.index.page1Title'),
      },
      ApplicationPage1,
    ),
    Loader,
  )

  const AppPage2 = withRouteGuard(
    withProgressMeter(
      {
        max: '5',
        value: '2',
        steps: getLocalizedText('application.index.step2Text'),
        title: getLocalizedText('application.index.page2Title'),
      },
      ApplicationPage2,
    ),
    Loader,
  )

  const AppPage3 = withRouteGuard(
    withProgressMeter(
      {
        max: '5',
        value: '3',
        steps: getLocalizedText('application.index.step3Text'),
        title: getLocalizedText('application.index.page3Title'),
      },
      ApplicationPage3,
    ),
    Loader,
  )

  const AppPage4 = withRouteGuard(
    withProgressMeter(
      {
        max: '5',
        value: '4',
        steps: getLocalizedText('application.index.step4Text'),
        title: getLocalizedText('application.index.page4Title'),
      },
      ApplicationPage4,
    ),
    Loader,
  )

  const AppPage5 = withRouteGuard(
    withProgressMeter(
      {
        max: '5',
        value: '5',
        steps: getLocalizedText('application.index.step5Text'),
        title: getLocalizedText('application.index.page5Title'),
      },
      ApplicationPage5,
    ),
    Loader,
  )

  // eslint-disable-next-line complexity
  const pageProps: any = (pageName: string) => {
    switch (pageName) {
      case 'applicationPage1':
        return {
          path: 'applicationPage1',
          applicationType,
          creditApplicationLoading,
        }
      case 'applicationPage2':
        return {
          path: 'applicationPage2',
          applicationType,
          creditApplicationLoading,
        }
      case 'applicationPage3':
        return {
          path: 'applicationPage3',
          applicationType,
          creditApplicationLoading,
        }
      case 'applicationPage4':
        return {
          path: 'applicationPage4',
          applicationType,
          creditApplicationLoading,
        }
      case 'applicationPage5':
        return {
          path: 'applicationPage5',
          applicationType,
          creditApplicationLoading,
        }
      case 'loading':
        return {
          path: 'loading',
          applicationType,
        }
      case 'offer':
        return {
          path: 'offer',
          applicationType,
        }
      case 'ineligible':
        return {
          path: 'ineligible',
          applicationType,
        }
      case 'creditFrozen':
        return {
          path: 'credit-frozen',
          applicationType,
        }
      case 'openApplication':
        return {
          path: 'open-application',
          applicationType,
        }
      case 'error':
        return {
          path: 'error',
          applicationType,
        }
      case 'thankYou':
        return {
          path: 'thank-you',
          applicationType,
        }
      case 'pending':
        return {
          path: 'pending',
          applicationType,
        }
      case 'invalidAccountType':
        return {
          path: 'invalid-account-type',
          applicationType,
        }
      case 'countryNotSupported':
        return {
          path: 'country-not-supported',
          applicationType,
        }
      case 'ineligibleState':
        return {
          path: 'ineligible-state',
          applicationType,
        }
      case 'sessionTimeout':
        return {
          path: 'session-timeout',
          applicationType,
        }
      default:
        return {
          applicationType,
        }
    }
  }

  const OffersPage = withRouteGuard(OfferPage, OfferLoader)

  const ThanksPage = withRouteGuard(ThankYouPage, Loader)

  const SubmissionLoadingPage = withRouteGuard(SubmissionLoading, Loader)

  let fetchedPrefillData = JSON.parse(
    sessionStorage.getItem('applicationPrefillData'),
  )

  useEffect(() => {
    if (creditApplicationData && !creditApplicationLoading) {
      clientLogger.info(
        'get application by id call successfull, updating context and cache - Application ID:',
        applicationId,
      )

      graphqlClient.writeQuery({
        query: getCreditApplicationDataQuery,
        data: fetchedPrefillData
          ? normalizeCreditApplicationResponse(
              merge({}, fetchedPrefillData, creditApplicationData),
            )
          : normalizeCreditApplicationResponse(creditApplicationData),
      })
      updateApplicationContext({
        applicationMetaData: {
          ...data?.applicationMetaData,
          mostRecentCompletedPage,
          applicationId:
            creditApplicationData?.credit?.businessCredit?.application?.id,
          legalBusinessName:
            creditApplicationData?.credit?.businessCredit?.application?.business
              ?.legalBusinessName,
          portalAccess: merge(
            data?.applicationMetaData?.portalAccess,
            creditApplicationData?.credit?.businessCredit?.application
              ?.portalAccess,
          ),
          businessRegion:
            creditApplicationData?.credit?.businessCredit?.application?.business
              ?.address?.adminArea1,
        },
        offerMetaData: {
          ...data?.offerMetaData,
          offerConstraints:
            creditApplicationData?.credit?.businessCredit?.application
              ?.evaluation?.underwriting?.offerConstraints,
          averageMonthlyGrossSales:
            creditApplicationData?.credit?.businessCredit?.application?.business
              ?.financialProfile?.averageMonthlyGrossSales,
          loanHistoryData:
            creditApplicationData?.credit?.businessCredit?.application?.business
              ?.loanHistory,
        },
      })
    } else if (creditApplicationError) {
      handleGraphqlError(
        creditApplicationError,
        'Error while calling GraphQl getApplication end point, redirecting to error page',
        'getApplicationById',
        applicationId,
        renewalToken,
        data?.applicationMetaData?.legalBusinessName,
        applicationType,
      )
    }
  }, [creditApplicationLoading])
  // eslint-disable-next-line complexity
  useEffect(() => {
    if (
      applicationType !== 'app' &&
      !prefillApplicationLoading &&
      !prefillApplicationError
    ) {
      if (
        (applicationType === 'app-renewal' ||
          applicationType === 'paypal-renewal') &&
        !renewalToken
      ) {
        clientLogger.warn(
          `Renewal token not present for application type ${applicationType}, redirecting to error page`,
        )
        navigate(
          `/merchant-lending/loanbuilder/application/us/${applicationType}/error`,
        )
      }
      //countrynoteligible
    }
    if (prefillApplicationData) {
      sessionStorage.setItem(
        'applicationPrefillData',
        JSON.stringify(prefillApplicationData),
      )
      sessionStorage.setItem('renewalTokenBurnt', 'true')
      fetchedPrefillData = prefillApplicationData
      clientLogger.info(
        'prefill API successfull - Renewal Token:',
        renewalToken,
      )
      const ineligibleState = applicationStateList.find(
        (state: any) =>
          state.value ===
          prefillApplicationData?.credit?.businessCredit?.applicationPrefillData
            ?.business?.incorporationRegion,
      )
      if (
        applicationType === 'paypal' &&
        prefillApplicationData?.credit?.businessCredit?.applicationPrefillData
          ?.business?.address?.countryCode !== 'US'
      ) {
        clientLogger.info(
          'Logged in User country is not US, redirecting to country not supported page',
          serverData?.user?.encryptedAccountNumber,
        )
        navigate(
          `/merchant-lending/loanbuilder/application/us/${applicationType}/country-not-supported`,
        )
      } else if (
        prefillApplicationData?.credit?.businessCredit?.applicationPrefillData
          ?.business?.incorporationRegion &&
        !ineligibleState
      ) {
        clientLogger.info(
          'Incorporation Region is not from supported list of states, redirecting to ineligible state page',
          serverData?.user?.encryptedAccountNumber,
        )
        navigate(
          `/merchant-lending/loanbuilder/application/us/${applicationType}/ineligible-state`,
        )
      }
    } else if (prefillApplicationError) {
      handleGraphqlError(
        prefillApplicationError,
        'Error while calling GraphQl prefillApplication end point, redirecting to error page',
        'prefillApplication',
        applicationId,
        renewalToken,
        data?.applicationMetaData?.legalBusinessName,
        applicationType,
      )
      clientLogger.info('prefill API renewal token', renewalToken)
    }

    if (fetchedPrefillData && !prefillApplicationLoading) {
      graphqlClient.writeQuery({
        query: getCreditApplicationDataQuery,
        data: normalizeCreditApplicationResponse(fetchedPrefillData),
      })
      updateApplicationContext({
        applicationMetaData: {
          ...data?.applicationMetaData,
          portalAccess: merge(
            data?.applicationMetaData?.portalAccess,
            fetchedPrefillData?.credit?.businessCredit?.applicationPrefillData
              ?.portalAccess,
          ),
          legalBusinessName:
            fetchedPrefillData?.credit?.businessCredit?.applicationPrefillData
              ?.business?.legalBusinessName,
        },
        offerMetaData: {
          ...data?.offerMetaData,
        },
      })
    }
  }, [prefillApplicationLoading])

  return (
    <div className="applicationContainer">
      <HeaderSection page="applicationPage" />
      <div className="application">
        {creditApplicationLoading || prefillApplicationLoading ? (
          <Router>
            <OfferLoader {...pageProps('offer')} />
            <Loader default />
          </Router>
        ) : (
          <Router>
            <AppPage1 {...pageProps('applicationPage1')} />
            <AppPage2 {...pageProps('applicationPage2')} />
            <AppPage3 {...pageProps('applicationPage3')} />
            <AppPage4 {...pageProps('applicationPage4')} />
            <AppPage5 {...pageProps('applicationPage5')} />
            <SubmissionLoadingPage {...pageProps('loading')} />
            <OffersPage {...pageProps('offer')} />
            <CreditFrozenPage {...pageProps('creditFrozen')} />
            <IneligiblePage {...pageProps('ineligible')} />
            <OpenApplicationPage {...pageProps('openApplication')} />
            <ErrorPage {...pageProps('error')} />
            <UnsupportedAccountPage {...pageProps('invalidAccountType')} />
            <CountryNotSupportedPage {...pageProps('countryNotSupported')} />
            <IneligibleStatePage {...pageProps('ineligibleState')} />
            <ThanksPage {...pageProps('thankYou')} />
            <PendingPage {...pageProps('pending')} />
            <SessionTimeout {...pageProps('sessionTimeout')} />
          </Router>
        )}
      </div>
      <FooterSection page="applicationPage" />
    </div>
  )
}

export default Application
